const gridSelector = (sectionId) => `#product-grid-${sectionId} [data-collection-product-grid]`;

export class CollectionLoadMore extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enable !== 'true') return;
    this.sectionId = this.dataset.sectionId;
    this.button = this.querySelector('[data-load-more-button]');
    this.label = this.querySelector('[data-load-more-label]');
    this.counter = this.querySelector('[data-load-more-counter]');
    if (!this.button || !this.counter) return;

    /** Cloned product-cards from the last fetch that could not fit in the previous chunk (chunk < page size). */
    this._pendingClones = [];
    /** Pagination snapshot from section HTML; applied only after _pendingClones is drained. */
    this._deferredMerge = null;

    this.onClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this.onClick);
    this.refreshLabel();
    this.updateButtonState();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  getGrid() {
    return document.querySelector(gridSelector(this.sectionId));
  }

  /** Shopify product cards only (excludes placeholder divs). Used for load-more / show-less logic. */
  getVisibleShopifyProductCount() {
    const grid = this.getGrid();
    if (!grid) return 0;
    return grid.querySelectorAll('product-card:not([hidden])').length;
  }

  refreshLabel() {
    const productTotal = Number(this.dataset.totalProducts) || 0;
    const placeholderTotal = Number(this.dataset.placeholderCount) || 0;
    const displayTotal = productTotal + placeholderTotal;
    const grid = this.getGrid();
    let viewed = 0;
    if (grid) {
      const products = grid.querySelectorAll('product-card:not([hidden])').length;
      const placeholders = grid.querySelectorAll('[data-placeholder-card]:not([hidden])').length;
      viewed = products + placeholders;
    }
    const safeViewed = Math.min(viewed, displayTotal);
    this.counter.textContent = `You've viewed ${safeViewed} of ${displayTotal} items`;
  }

  syncCounterWithButton() {
    if (!this.counter || !this.button) return;
    this.counter.hidden = this.button.hidden;
  }

  updateButtonState() {
    if (!this.button || !this.counter) return;
    const total = Number(this.dataset.totalProducts) || 0;
    const initial = Number(this.dataset.initial) || 0;
    const viewed = this.getVisibleShopifyProductCount();
    const nextUrl = (this.dataset.nextUrl || '').trim();
    const hasBuffers = Boolean(this.getGrid()?.querySelector('product-card[data-lm-buffer="true"]'));
    const hasPending = Array.isArray(this._pendingClones) && this._pendingClones.length > 0;

    if (initial < 1 || total <= initial) {
      this.button.hidden = true;
      this.button.dataset.state = 'more';
      if (this.label) this.label.textContent = 'Load more';
      this.button.setAttribute('aria-label', 'Load more products');
      this.syncCounterWithButton();
      return;
    }

    this.button.hidden = false;

    const allShown = viewed >= total && total > 0;
    if (allShown) {
      this.button.dataset.state = 'less';
      if (this.label) this.label.textContent = 'Show less';
      this.button.setAttribute('aria-expanded', 'true');
      this.button.setAttribute('aria-label', 'Show fewer products');
    } else {
      this.button.dataset.state = 'more';
      if (this.label) this.label.textContent = 'Load more';
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-label', 'Load more products');
    }

    if (!allShown && !hasBuffers && !nextUrl && !hasPending) {
      this.button.hidden = true;
    }

    this.syncCounterWithButton();
  }

  async handleClick(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-busy') === 'true' || this.button.disabled) return;

    if (this.button.dataset.state === 'less') {
      await this.runShowLess();
    } else {
      await this.runLoadMore();
    }
  }

  revealBuffers(maxCount) {
    const grid = this.getGrid();
    if (!grid || maxCount <= 0) return 0;
    let revealed = 0;
    const nodes = grid.querySelectorAll('product-card[data-lm-buffer="true"]');
    for (const el of nodes) {
      if (revealed >= maxCount) break;
      el.removeAttribute('hidden');
      el.removeAttribute('data-lm-buffer');
      revealed += 1;
    }
    return revealed;
  }

  async fetchSectionPage(searchParamsString) {
    const url = `${window.location.pathname}?${searchParamsString}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Collection fetch failed: ${res.status}`);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  }

  async fetchNextPageDoc() {
    const nextUrl = (this.dataset.nextUrl || '').trim();
    if (!nextUrl) return null;
    const resolved = new URL(nextUrl, window.location.origin);
    resolved.searchParams.set('section_id', this.sectionId);
    return this.fetchSectionPage(resolved.searchParams.toString());
  }

  mergeLoadMoreStateFromDoc(doc) {
    const sid = this.sectionId;
    const src = doc.getElementById(`collection-load-more-${sid}`);
    if (!src) return;
    this._deferredMerge = null;
    this.dataset.nextUrl = src.dataset.nextUrl || '';
    this.dataset.currentPage = src.dataset.currentPage || '1';
    this.dataset.totalPages = src.dataset.totalPages || '1';
    if (src.dataset.totalProducts) {
      this.dataset.totalProducts = src.dataset.totalProducts;
    }
    this.dataset.placeholderCount = src.dataset.placeholderCount || '0';
  }

  captureDeferredPaginationFromDoc(doc) {
    const sid = this.sectionId;
    const src = doc.getElementById(`collection-load-more-${sid}`);
    if (!src) return;
    this._deferredMerge = {
      nextUrl: src.dataset.nextUrl || '',
      currentPage: src.dataset.currentPage || '1',
      totalPages: src.dataset.totalPages || '1',
      totalProducts: src.dataset.totalProducts,
      placeholderCount: src.dataset.placeholderCount,
    };
    this.dataset.nextUrl = '';
  }

  applyDeferredPaginationIfPendingEmpty() {
    if (!Array.isArray(this._pendingClones) || this._pendingClones.length > 0) return;
    if (!this._deferredMerge) return;
    this.dataset.nextUrl = this._deferredMerge.nextUrl || '';
    this.dataset.currentPage = this._deferredMerge.currentPage || '1';
    this.dataset.totalPages = this._deferredMerge.totalPages || '1';
    if (this._deferredMerge.totalProducts) {
      this.dataset.totalProducts = this._deferredMerge.totalProducts;
    }
    if (this._deferredMerge.placeholderCount !== undefined) {
      this.dataset.placeholderCount = this._deferredMerge.placeholderCount || '0';
    }
    this._deferredMerge = null;
  }

  /**
   * Append up to maxAppend product-cards from the fetched section grid.
   * Overflow clones are queued for later clicks when chunk < Shopify page size.
   */
  ingestFetchedGrid(doc, maxAppend) {
    const sid = this.sectionId;
    const srcGrid = doc.querySelector(gridSelector(sid));
    const destGrid = this.getGrid();
    if (!srcGrid || !destGrid || maxAppend <= 0) return 0;

    let appended = 0;
    const candidates = Array.from(srcGrid.children).filter((n) => n.matches?.('product-card'));

    for (const node of candidates) {
      const pid = node.getAttribute('data-product-id');
      if (pid && destGrid.querySelector(`product-card[data-product-id="${pid}"]`)) continue;

      const clone = document.importNode(node, true);
      clone.setAttribute('data-lm-appended', 'true');

      if (appended >= maxAppend) {
        this._pendingClones.push(clone);
        continue;
      }
      destGrid.appendChild(clone);
      appended += 1;
    }

    return appended;
  }

  setLoading(isLoading) {
    if (!this.button) return;
    if (isLoading) {
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
    } else {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
    }
  }

  async runLoadMore() {
    const chunk = Number(this.dataset.chunk) || 12;
    this.setLoading(true);

    try {
      this.applyDeferredPaginationIfPendingEmpty();

      let remaining = chunk;
      remaining -= this.revealBuffers(remaining);

      let guard = 0;
      while (remaining > 0 && guard < 80) {
        guard += 1;

        const destGrid = this.getGrid();
        if (!destGrid) break;

        while (remaining > 0 && this._pendingClones.length) {
          destGrid.appendChild(this._pendingClones.shift());
          remaining -= 1;
        }
        this.applyDeferredPaginationIfPendingEmpty();
        if (remaining <= 0) break;

        if (!this._pendingClones.length && !(this.dataset.nextUrl || '').trim()) break;

        const doc = await this.fetchNextPageDoc();
        if (!doc) break;

        const beforeAppend = this.getVisibleShopifyProductCount();
        const added = this.ingestFetchedGrid(doc, remaining);
        remaining -= added;

        if (this._pendingClones.length > 0) {
          this.captureDeferredPaginationFromDoc(doc);
        } else {
          this.mergeLoadMoreStateFromDoc(doc);
        }

        const afterAppend = this.getVisibleShopifyProductCount();
        if (added === 0 && this._pendingClones.length === 0) break;
        if (afterAppend <= beforeAppend && added === 0) break;
      }
    } catch (e) {
      console.error('[collection-load-more]', e);
    } finally {
      this.setLoading(false);
      this.refreshLabel();
      this.updateButtonState();
    }
  }

  async runShowLess() {
    const sid = this.sectionId;
    this.setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('page', '1');
      params.set('section_id', sid);

      const doc = await this.fetchSectionPage(params.toString());
      const srcGrid = doc.querySelector(gridSelector(sid));
      const destGrid = this.getGrid();
      if (srcGrid && destGrid) {
        destGrid.innerHTML = srcGrid.innerHTML;
      }

      const srcLm = doc.getElementById(`collection-load-more-${sid}`);
      if (srcLm) {
        const imported = document.importNode(srcLm, true);
        this.replaceWith(imported);
      }

      const gridEl = document.getElementById(`product-grid-${sid}`);
      gridEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const replacement = document.getElementById(`collection-load-more-${sid}`);
      replacement?.refreshLabel();
      replacement?.updateButtonState();
    } catch (e) {
      console.error('[collection-load-more] show less', e);
    } finally {
      const live = document.getElementById(`collection-load-more-${sid}`);
      const btn = live?.querySelector('[data-load-more-button]');
      btn?.removeAttribute('aria-busy');
      if (btn) btn.disabled = false;
    }
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}
